import { graphql, useStaticQuery } from 'gatsby'
import { RichText } from 'prismic-reactjs'
import React from 'react'

import htmlSerializer, { linkResolver } from '@somo/oxd-apps-www/src/utils/html-serializer'
import { FEATURES, hasFeatureFlag } from '@cinch/oxd-utils/feature-flags/src'
import OxfordPage from '@cinch/oxd-pages/our-commitment/src'
import Page from '@cinch/oxd-pages/cambridge/our-commitment/src'

import SEO, { Breadcrumb } from '../components/seo'
import { getEnv, Env } from '@cinch/oxd-utils/env/src'

const renderContent = (content: any) => RichText.render(content, linkResolver, htmlSerializer)

const CinchCommitment: React.FC<Cinch.SitePage> = ({ location }) => {
  const data: any = useStaticQuery(graphql`
    query {
      prismic {
        promise(uid: "cinch", lang: "en-gb") {
          bullets
          confidencetitle
          columns {
            title
            body
            icon
          }
          vehicle_features {
            title
            body
            icon
          }
          trusted_dealers_title
          trusted_dealers_body
        }
        commitment_direct(uid: "cinch", lang: "en-gb") {
          commitmenttitle
          commitment_points {
            title
            body
            icon
            cta_text
            cta_link
          }
          trusted_dealers_title
          trusted_dealers_body
        }
      }
      allTranslations {
        edges {
          node {
            ourCommitmentPage {
              heroTitle
              heroBody
              register
              find
              heroImageAlt
              chooseToBuyOnlineTitle
              chooseToBuyOnlineBullets
              chooseToBuyOnlineCtaText
              chooseToBuyOnlineCtaText
              needHelpChoosingTitle
              needHelpChoosingBody
              needHelpChoosingCtaText
              dealershipsYouCanTrustTitle
              dealershipsYouCanTrustBody
              dealershipsYouCanTrustCtaText
            }
          }
        }
      }
      largeHeroImage: file(relativePath: { eq: "car-next-to-front-door.png" }) {
        childImageSharp {
          fluid(maxWidth: 1194, quality: 80) {
            src
            ...GatsbyImageSharpFluid_withWebp_noBase64
          }
        }
      }
      smallHeroImage: file(relativePath: { eq: "car-next-to-front-door.png" }) {
        childImageSharp {
          fluid(maxWidth: 1194, quality: 80) {
            src
            ...GatsbyImageSharpFluid_withWebp_noBase64
          }
        }
      }
    }
  `)

  const i18n = data.allTranslations.edges[0].node.ourCommitmentPage
  const { promise, commitment_direct } = data.prismic
  const breadcrumbs: Breadcrumb[] = []

  if (location) {
    const pageUrl = `${getEnv(Env.SiteUrl)}${location.pathname}`
    breadcrumbs.push({
      name: i18n.title,
      item: pageUrl,
    })
  }

  const heroImage = {
    large: {
      webp: data.largeHeroImage.childImageSharp.fluid.srcWebp,
      png: data.largeHeroImage.childImageSharp.fluid.src,
    },
    small: {
      webp: data.smallHeroImage.childImageSharp.fluid.srcWebp,
      png: data.smallHeroImage.childImageSharp.fluid.src,
    },
    alt: i18n.heroImageAlt,
  }

  return (
    <div>
      <SEO title={i18n.title} breadcrumbs={breadcrumbs} />
      {hasFeatureFlag(FEATURES.CAMBRIDGE) ? (
        <Page
          i18n={i18n}
          heroImage={heroImage}
          commitment={{
            commitmentTitle: commitment_direct.commitmenttitle,
            content: commitment_direct.commitment_points.map((cp: any) => ({
              title: cp.title,
              body: renderContent(cp.body),
              icon: cp.icon,
              ctaText: cp.cta_text,
              ctaLink: cp.cta_link,
            })),
          }}
        />
      ) : (
        <OxfordPage
          i18n={i18n}
          bullets={renderContent(promise.bullets)}
          confidence={{
            title: promise.confidencetitle,
            content: promise.columns.map((column: any) => ({
              title: column.title,
              body: renderContent(column.body),
              icon: column.icon,
            })),
          }}
          vehicleDetails={promise.vehicle_features.map((feature: any) => ({
            title: feature.title,
            body: renderContent(feature.body),
            icon: feature.icon,
          }))}
          trustedDealers={{
            title: promise.trusted_dealers_title,
            body: renderContent(promise.trusted_dealers_body),
          }}
        />
      )}
    </div>
  )
}

export default CinchCommitment
